import { Injectable, REQUEST_CONTEXT, inject } from '@angular/core';

import { isTransientFetchFailure } from '../api/fetch-failure';

/**
 * Mutable per-request box shared between the Node server and the Angular app.
 *
 * `server.ts` creates one of these, passes it into `angularApp.handle(req, ctx)`, and reads it back
 * after rendering completes. Components mutate it through `SsrResponseService`.
 */
export interface SsrResponseContext {
  status: number;
  /** When set, the server responds with a 301 to this location instead of the rendered HTML. */
  redirectTo?: string;
}

export function createSsrResponseContext(): SsrResponseContext {
  return { status: 200 };
}

/**
 * Lets a page set the HTTP status / redirect of the server response it is being rendered into.
 *
 * Without this, an unknown slug renders the app's "not found" state and still returns **200** — a
 * soft 404, which is exactly what spec §7 forbids. Angular has no direct way for a component to
 * reach the response, so we round-trip through REQUEST_CONTEXT: the component records its intent
 * here, and `server.ts` applies it once rendering has finished.
 *
 * In the browser every method is a no-op (there is no response to mutate), so pages can call these
 * unconditionally.
 */
@Injectable({ providedIn: 'root' })
export class SsrResponseService {
  private readonly context = inject(REQUEST_CONTEXT, { optional: true }) as
    | SsrResponseContext
    | null
    | undefined;

  /**
   * True once the page's primary fetch has failed for a reason that is not "no such resource".
   * Consumed by `setNotFound`, and readable by pages that want to word their failure state
   * correctly rather than saying "not found" about something that may well exist.
   */
  private transientFailure = false;

  /**
   * Records why a primary fetch failed, and reports whether it was transient.
   *
   * Call this from the `catchError` of the request the page's not-found decision depends on. Pages
   * resolve both outcomes to `null` and cannot tell them apart afterwards; this keeps the cause.
   */
  noteFetchFailure(error: unknown): boolean {
    const transient = isTransientFetchFailure(error);
    if (transient) this.transientFailure = true;
    return transient;
  }

  /** Lets a page choose failure-state copy without duplicating the classification. */
  isTransientFailure(): boolean {
    return this.transientFailure;
  }

  /**
   * Marks the current render as a real 404 (spec §7 — no soft 404s) — unless the page only reached
   * this point because its primary fetch could not be completed, in which case the honest answer is
   * 503.
   *
   * The distinction is the whole point: 404 tells Google the URL is gone and it gets dropped, while
   * 503 tells it to come back. A crawl on 2026-08-29 recorded 220 store pages as 404 that were live
   * the entire time, purely because a slow API and a missing slug both arrived here as `null`.
   *
   * The flag is consumed on read so a transient failure on one page cannot leak into the next
   * navigation's genuine 404. On the server that cannot happen anyway — each request builds its own
   * injector — but in the browser this service is a singleton for the life of the tab.
   */
  setNotFound(): void {
    const transient = this.transientFailure;
    this.transientFailure = false;
    if (this.context) this.context.status = transient ? 503 : 404;
  }

  /** Marks the current render as gone — a deleted entity with no replacement. */
  setGone(): void {
    if (this.context) this.context.status = 410;
  }

  /**
   * Marks the render as temporarily unavailable — the entity may well exist, but the API could not
   * be reached to confirm it.
   *
   * This exists so a backend hiccup cannot be reported as a missing page. 404 tells Google the URL
   * is gone and it drops it from the index; 503 tells Google to retry, and nothing is lost. The SSR
   * cache only stores 200s (`isCacheable`), so a 503 is never pinned for the next visitor either.
   */
  setUnavailable(): void {
    if (this.context) this.context.status = 503;
  }

  setStatus(status: number): void {
    if (this.context) this.context.status = status;
  }

  /**
   * Permanently redirects to `location` (e.g. a non-canonical comparison order like
   * `/compare/shein-vs-zaful` -> `/compare/zaful-vs-shein`).
   */
  redirectPermanent(location: string): void {
    if (this.context) {
      this.context.status = 301;
      this.context.redirectTo = location;
    }
  }
}
